import React, { useState, useCallback, useEffect } from 'react';
import { Mail, ExternalLink } from 'lucide-react';
import { openDrawer } from 'Services/Drawer';
import T from 'Components/Helpers/translate';
import AssistantResource from 'Resources/AssistantChat';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import { sanitizeMailHtml } from 'App/helpers/mailSanitize';
import { executionFailureMessage } from './approvalAdapters';
import EmailDraftAttachments from './EmailDraftAttachments';
import type { EmailDraft } from './types';

const extractMailIds = (raw: unknown): number[] | undefined => {
	if (!raw || typeof raw !== 'object') return undefined;
	const ids = (raw as { mailIds?: unknown }).mailIds;
	if (!Array.isArray(ids)) return undefined;
	const out = ids.map(v => Number(v)).filter(n => Number.isFinite(n));
	return out.length ? out : undefined;
};

type Props = {
	draft: EmailDraft;
	onUpdate?: (update: Partial<EmailDraft>) => void;
	/** When true, the card renders in a fully inert state — no send /
	 *  cancel buttons, no inputs are editable. Used by the trace drawer
	 *  when displaying an already-resolved approval. */
	readOnly?: boolean;
};

type ResolvedContact = { id: number; name: string; email: string };

const resolveContacts = async (ids: number[]): Promise<ResolvedContact[]> => {
	if (!ids.length) return [];
	const results: ResolvedContact[] = [];
	await Promise.all(
		ids.map(async id => {
			try {
				const res = await fetch(`/api/v2/contacts/${id}`);
				if (!res.ok) return;
				const body = await res.json();
				const d = body.data;
				const name = d?.name || [d?.firstName, d?.lastName].filter(Boolean).join(' ') || `#${id}`;
				results.push({ id, name, email: d?.email || '' });
			} catch {
				results.push({ id, name: `#${id}`, email: '' });
			}
		})
	);
	return results;
};

function statusToPill(status: EmailDraft['status']): StatusPill | null {
	switch (status) {
		case 'sending':
			return { label: T('assistant.emailSending') || 'Sending…', tone: 'saving' };
		case 'sent':
			return { label: T('assistant.emailSent') || 'Sent', tone: 'ok' };
		case 'cancelled':
			return { label: T('assistant.cancelled') || 'Cancelled', tone: 'muted' };
		case 'error':
			return { label: T('default.failed') || 'Failed', tone: 'error' };
		case 'pending':
		default:
			return null;
	}
}

const EmailDraftCard = ({ draft, onUpdate, readOnly = false }: Props) => {
	const [editedSubject, setEditedSubject] = useState(draft.subject);
	const [editedBody, setEditedBody] = useState(draft.body);
	const [toContacts, setToContacts] = useState<ResolvedContact[]>([]);
	const [ccContacts, setCcContacts] = useState<ResolvedContact[]>([]);
	const [signatureHtml, setSignatureHtml] = useState<string | null>(null);
	const [attachments, setAttachments] = useState(draft.attachments ?? []);
	const [uploading, setUploading] = useState(false);

	const isSending = draft.status === 'sending';
	const resolved = draft.status === 'sent' || draft.status === 'cancelled';
	const errored = draft.status === 'error';

	useEffect(() => {
		let active = true;
		void resolveContacts(draft.contactIds).then(contacts => {
			if (active) setToContacts(contacts);
		});
		if (draft.ccContactIds?.length) {
			void resolveContacts(draft.ccContactIds).then(contacts => {
				if (active) setCcContacts(contacts);
			});
		}
		return () => {
			active = false;
		};
	}, [draft.contactIds.join(','), draft.ccContactIds?.join(',')]);

	// The signature is shown below the message, rendered, and never merged into
	// the editable body (WEB-11438): a textarea can only hold text, so merging
	// it meant flattening away the logo, the styling and every link — including
	// the privacy-policy link some customers are required to carry. The endpoint
	// returns the body with its {{General.*}} tags already expanded by the same
	// helper the send path uses, so the preview resolves to the same signature
	// the recipient will get. It is not a pixel-exact proof of it: the send
	// ships `signature.body` as stored, while what we render here has been
	// through `sanitizeMailHtml`, which drops <style> blocks, unwraps tags
	// outside its allowlist and rejects `data:` URLs — so a signature built on
	// an embedded base64 logo or a <style> block previews plainer than it
	// arrives. Erring towards under-rendering is deliberate; this is untrusted
	// stored HTML going into dangerouslySetInnerHTML. Only for a card that can
	// still be sent: a resolved one shows what was sent, and today's signature
	// is not that.
	useEffect(() => {
		if (resolved || readOnly) return;
		let active = true;
		void fetch('/api/v2/mailSignature/active-for-user')
			.then(r => {
				if (!r.ok) throw new Error(`mailSignature/active-for-user responded ${r.status}`);
				return r.json();
			})
			.then(body => {
				if (!active) return;
				const html = body?.data?.body;
				setSignatureHtml(typeof html === 'string' && html.trim() ? sanitizeMailHtml(html) : null);
			})
			.catch(err => {
				// Below the unmount guard on purpose. The request is not aborted on
				// unmount — we simply drop its outcome — and a card that is gone has
				// no preview to be wrong about.
				if (!active) return;
				// The server still appends the signature at send time, so the mail
				// is signed either way — but the preview is then wrong, and silence
				// would make that impossible to diagnose.
				console.error('[EmailDraftCard] could not load the signature preview', err);
				setSignatureHtml(null);
			});
		return () => {
			active = false;
		};
	}, [resolved, readOnly]);

	const handleSend = useCallback(async () => {
		if (!onUpdate) return;
		onUpdate({ status: 'sending' });
		try {
			let mailIds: number[] | undefined;
			let failure: string | null = null;
			if (draft.approvalId) {
				const modified = {
					contactIds: draft.contactIds,
					subject: editedSubject,
					body: editedBody,
					ccContactIds: draft.ccContactIds,
					clientId: draft.clientId,
					// The executor runs the modified payload, so this is what gets attached
					// (WEB-13259: the rep may add or remove files). The descriptors are
					// what a reloaded card lists, as on the stored payload.
					attachmentFileIds: attachments.map(a => a.id),
					attachments
				};
				const { approval } = await AgentApprovalsResource.approve(draft.approvalId, undefined, modified);
				mailIds = extractMailIds(approval.resultPayload);
				failure = executionFailureMessage(approval.resultPayload as Record<string, unknown> | null | undefined);
			} else {
				const result = await AssistantResource.executeSendEmail(
					draft.contactIds,
					editedSubject,
					editedBody,
					draft.ccContactIds,
					draft.clientId
				);
				mailIds = extractMailIds(result);
				failure = executionFailureMessage(result as Record<string, unknown> | null | undefined);
			}
			if (failure !== null) {
				onUpdate({
					status: 'error',
					subject: editedSubject,
					body: editedBody,
					error: failure || T('default.failed') || 'Send failed'
				});
			} else {
				onUpdate({ status: 'sent', subject: editedSubject, body: editedBody, mailIds });
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Send failed';
			onUpdate({ status: 'error', error: message });
		}
	}, [
		draft.approvalId,
		draft.contactIds,
		draft.ccContactIds,
		draft.clientId,
		attachments,
		editedSubject,
		editedBody,
		onUpdate
	]);

	const handleCancel = useCallback(async () => {
		if (!onUpdate) return;
		if (draft.approvalId) {
			try {
				// Persist the user's edits on reject too, so a reload of the
				// rejected card surfaces what the rep actually saw — not the
				// agent's original draft text.
				const editsDiffer = editedSubject !== draft.subject || editedBody !== draft.body;
				const modified = editsDiffer
					? {
							contactIds: draft.contactIds,
							subject: editedSubject,
							body: editedBody,
							ccContactIds: draft.ccContactIds,
							clientId: draft.clientId
					  }
					: undefined;
				await AgentApprovalsResource.reject(draft.approvalId, 'cancelled_in_chat', modified);
			} catch {
				/* non-fatal — local state still flips to cancelled */
			}
		}
		onUpdate({ status: 'cancelled' });
	}, [
		draft.approvalId,
		draft.subject,
		draft.body,
		draft.contactIds,
		draft.ccContactIds,
		draft.clientId,
		editedSubject,
		editedBody,
		onUpdate
	]);

	// A failed approval row is terminal server-side, so re-arming the card locally
	// just sends the next click into the resolve guard's `approval_rejected`. The
	// retry endpoint re-executes a resolved row, and it re-uses the row's stored
	// `modifiedPayload` — so the subject/body edits the first attempt persisted
	// are carried through (WEB-9873).
	const handleRetry = useCallback(async () => {
		if (!onUpdate) return;
		if (!draft.approvalId) {
			onUpdate({ status: 'pending', error: undefined });
			return;
		}
		onUpdate({ status: 'sending', error: undefined });
		try {
			const { approval } = await AgentApprovalsResource.retry(draft.approvalId);
			const failure = executionFailureMessage(
				approval.resultPayload as Record<string, unknown> | null | undefined
			);
			if (failure !== null) {
				onUpdate({ status: 'error', error: failure || T('default.failed') || 'Send failed' });
			} else {
				onUpdate({ status: 'sent', mailIds: extractMailIds(approval.resultPayload) });
			}
		} catch (err) {
			onUpdate({ status: 'error', error: err instanceof Error ? err.message : 'Retry failed' });
		}
	}, [draft.approvalId, onUpdate]);

	const recipientLabelShort = toContacts.length
		? toContacts.map(c => c.name).join(', ')
		: draft.contactIds.map(id => `#${id}`).join(', ');
	const recipientLabelLong = toContacts.length
		? toContacts.map(c => (c.email ? `${c.name} <${c.email}>` : c.name)).join(', ')
		: draft.contactIds.map(id => `#${id}`).join(', ');
	const ccLabel = ccContacts.length ? ccContacts.map(c => c.name).join(', ') : null;

	const titleParts = [T('mail.mail') || 'Email'];
	if (recipientLabelShort) titleParts.push(recipientLabelShort);
	if (editedSubject.trim()) titleParts.push(editedSubject.trim());
	const title = titleParts.join(' · ');

	const inputsDisabled = isSending || readOnly || resolved;

	// WEB-10524: a payload whose recipient list the adapter could not read
	// arrives here as an empty list. Say so and withhold Send, rather than
	// offering a normal editable card whose To field happens to be blank — that
	// card looks sendable, and approving it trips the recipients-immutability
	// guard with an error about changing recipients the user never touched.
	const noRecipients = !draft.contactIds.length;

	const body = (
		<div className="pr-email-body">
			{errored && draft.error && <div className="pr-email-error">{draft.error}</div>}
			{noRecipients && !resolved ? (
				<div className="pr-email-error">{T('assistant.emailNoRecipients')}</div>
			) : null}
			<dl className="pr-email-fields">
				<dt>{T('mail.to') || 'To'}</dt>
				<dd>{recipientLabelLong}</dd>
				{ccLabel && (
					<>
						<dt>{T('mail.cc') || 'CC'}</dt>
						<dd>{ccLabel}</dd>
					</>
				)}
				<dt>{T('mail.subject') || 'Subject'}</dt>
				<dd>
					<input
						type="text"
						className="pr-email-input"
						value={editedSubject}
						onChange={e => setEditedSubject(e.target.value)}
						disabled={inputsDisabled}
					/>
				</dd>
				<dt className="pr-email-label-multiline">{T('mail.body') || 'Message'}</dt>
				<dd>
					<textarea
						className="pr-email-input pr-email-textarea"
						value={editedBody}
						onChange={e => setEditedBody(e.target.value)}
						disabled={inputsDisabled}
						rows={Math.min(16, Math.max(6, editedBody.split('\n').length + 1))}
					/>
				</dd>
				{signatureHtml && !resolved && !readOnly && (
					<>
						<dt className="pr-email-label-multiline">{T('default.signature')}</dt>
						<dd>
							<div className="pr-email-signature" dangerouslySetInnerHTML={{ __html: signatureHtml }} />
						</dd>
					</>
				)}
				{/* Only an approval row can carry attachments the rep changed. */}
				<EmailDraftAttachments
					attachments={attachments}
					onChange={setAttachments}
					editable={!inputsDisabled && !errored && !!draft.approvalId}
					onUploadingChange={setUploading}
				/>
			</dl>
		</div>
	);

	const canApprove =
		!readOnly &&
		!resolved &&
		!noRecipients &&
		(errored || (!isSending && !uploading && editedSubject.trim() && editedBody.trim()));

	const firstMailId = draft.mailIds?.[0];
	const handleOpenMail = useCallback(() => {
		if (firstMailId != null) openDrawer('SentMail', { mail: { id: firstMailId } });
	}, [firstMailId]);
	const headerAction =
		draft.status === 'sent' && firstMailId != null ? (
			<button
				type="button"
				className="ws-btn ws-btn-secondary ws-prop-mini-btn"
				onClick={handleOpenMail}
				title={T('default.entity.view', { entity: T('mail.mail') })}
			>
				<ExternalLink style={{ width: 12, height: 12 }} />
				<span>{T('default.open')}</span>
			</button>
		) : null;

	return (
		<div className="pr-email-card-wrapper">
			<ProposalCardShell
				icon={Mail}
				title={title}
				chips={draft.createdDate ? [draft.createdDate] : undefined}
				statusPill={statusToPill(draft.status)}
				headerAction={headerAction}
				body={body}
				busy={isSending}
				resolved={resolved}
				approveLabel={errored ? T('assistant.retry') : T('assistant.emailSend') || 'Send'}
				rejectLabel={T('cancel') || T('assistant.cancel')}
				onApprove={canApprove ? (errored ? handleRetry : handleSend) : undefined}
				onReject={readOnly || resolved || errored ? undefined : handleCancel}
			/>
		</div>
	);
};

export default EmailDraftCard;
